import cloneDeep from "lodash/cloneDeep";
import { getTokenHeader } from "@/helpers/commonHelper";
import axios from "axios";
import { confirmWarningConfig } from "@/configuration/confirmConfigurations";
import { debounce } from "lodash";
import TreeViewSelection from "@/components/Common/TreeViewSelection/TreeViewSelection.vue";
import ValidationDialog from "@/components/Common/ValidationDialog/ValidationDialog.vue";
import EventImagePreview from "@/components/Events/EventImagePreview/EventImagePreview.vue";
import KisDefectList from "@/components/KIS/KisDefectList/KisDefectList.vue";
import moment from "moment/moment";
import LocationMap from "@/components/Common/LocationMap/LocationMap.vue";
import KisDefectEditDialog from "@/components/KIS/KisDefectEditDialog/KisDefectEditDialog.vue";
import { getState } from "@/helpers/kisHelper";
import DefectFilter from "@/components/KIS/DefectFilter/DefectFilter.vue";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);

const defaultItem = {
  name: "",
  description: "",
  active: true,
  location: { address: "", city: "", zip: "", geo: { coordinates: [] } }
};

export default {
  name: "KisDefect",
  components: {
    DefectFilter,
    KisDefectEditDialog,
    LocationMap,
    KisDefectList,
    EventImagePreview,
    ValidationDialog,
    TreeViewSelection
  },
  props: ["editId"],
  data: () => ({
    loading: false,
    editedItem: {
      location: { address: "", city: "", zip: "", geo: { coordinates: [] } }
    },
    editDialog: false,
    previewDialog: false,
    previewItem: {},
    filter: { name: "", limit: 10 },
    headers: [
      { text: "Name", value: "name" },
      { text: "Zuletzt geändert am", value: "updatedAt" },
      { text: "Aktiv", value: "active" },
      { text: "Aktionen", sortable: false, value: "actions" }
    ]
  }),
  async mounted() {
    this.loading = true;
    const itemsPerPage = this.itemsPerPage;
    if (itemsPerPage && !isNaN(itemsPerPage)) {
      this.filter.limit = itemsPerPage;
      this.filter.itemsPerPage = itemsPerPage;
      this.itemsPerPage = itemsPerPage;
    }
    await this.$store.dispatch("getAllUsers");
    await this.$store.dispatch("getDefects", this.filter);
    await this.$store.dispatch("getDefectCategories");

    if (this.editId) {
      const item = await this.getItem(this.editId);
      if (item?._id) this.editItem(item);
    }
    this.loading = false;
  },
  methods: {
    getState,
    editItem(item) {
      this.editDialog = true;
      this.editedItem = cloneDeep(item);
    },
    closeDialog() {
      this.editedItem = cloneDeep(defaultItem);
      this.editDialog = false;
    },
    setFilterPage(page) {
      this.filter.page = page;
    },
    setFilterLimit(limit) {
      this.filter.limit = limit;
      this.filter.itemsPerPage = limit;
      localStorage.setItem("itemsPerPage", limit);
    },
    async setFilter(filter) {
      this.filter = filter;
      await this.$store.dispatch("getDefects", this.filter);
    },
    async resetFilter() {
      this.filter = {};
      const itemsPerPage = this.itemsPerPage;
      if (itemsPerPage && !isNaN(itemsPerPage)) {
        this.filter.limit = itemsPerPage;
        this.filter.itemsPerPage = itemsPerPage;
        this.itemsPerPage = itemsPerPage;
      }
      await this.$store.dispatch("getDefects", this.filter);
    },
    openPreviewDialog(item) {
      this.previewDialog = true;
      this.previewItem = item;
    },
    getNiceDateTime(value) {
      return moment(value).format("DD.MM.YYYY HH:mm");
    },
    getCategory(category) {
      return (
        this.defectCategories?.find(cat => cat._id === category)?.title ?? ""
      );
    },
    getResponsiblePersons(persons) {
      let mappedPersons = persons.map(_person =>
        this.persons.find(p => _person === p._id)
      );

      mappedPersons = mappedPersons.map(person => {
        return `${person.first_name} ${person.last_name}`;
      });
      return mappedPersons.join(", ");
    },
    closePreviewDialog() {
      this.previewDialog = false;
      this.previewItem = {};
    },
    async getItem(id) {
      try {
        const headers = getTokenHeader();
        const response = await axios.get(
          `${env.kis_api_client_url}defect/${id}`,
          {
            params: { principal_id: this.currentPrincipal },
            headers
          }
        );
        return response?.data ?? {};
      } catch (e) {
        await this.$store.dispatch("createError", e);
      }
    },
    async deleteDefect(item) {
      const confirm = await this.$confirm(
        "Wollen Sie diese Mangelmeldung wirklich löschen?",
        confirmWarningConfig
      );
      if (confirm) {
        try {
          const headers = getTokenHeader();
          const response = await axios.delete(
            `${env.kis_api_client_url}defect/${item._id}`,
            {
              params: { principal_id: this.currentPrincipal },
              headers: headers
            }
          );
          if (response.status === 200) {
            await this.$store.dispatch("createSuccess", {
              text: "Erfolgreich gelöscht"
            });
            this.$store.dispatch("getDefects", this.filter);
          } else {
            this.$store.dispatch("createError", {
              text: "Es ist ein unerwarteter Fehler aufgetreten"
            });
          }
        } catch (e) {
          await this.$store.dispatch("createError", {
            text: "Es ist ein unerwarteter Fehler aufgetreten"
          });
        }
      }
    },
    cancelEdit() {
      this.editedItem = cloneDeep(defaultItem);
      this.$refs.editView?.$refs?.logOverview.resetLogs();
      this.editDialog = false;
      if (this.editId) window.location.href = "/defects/defects";
    },
    async saveDefect(close = false) {
      const headers = getTokenHeader();
      const response = await axios.put(
        `${env.kis_api_client_url}defect/${this.editedItem._id}`,
        this.editedItem,
        {
          headers
        }
      );
      if (response.status === 200) {
        await this.$store.dispatch("createSuccess", "Erfolgreich gespeichert");
        await this.$store.dispatch("getDefects", this.filter);

        this.editedItem = cloneDeep(response.data);
      } else {
        await this.$store.dispatch(
          "createError",
          "Es ist ein unerwarteter Fehler aufgetreten"
        );
      }

      const logOverviewRef = this.$refs.editView?.$refs?.logOverview;
      const logsFetched = logOverviewRef?.logsFetched;
      logOverviewRef?.resetLogs();

      if (close) {
        this.cancelEdit();
      } else if (logsFetched) {
        await logOverviewRef?.getLogs();
      }
    },
    async getItems(filter) {
      await this.$store.dispatch("getDefects", filter);
    },
    applySearchText: debounce(async function() {
      if (this.filter.search.length >= 3 || !this.filter.search.length)
        await this.$store.dispatch("getDefects", this.filter);
    }, 500)
  },

  computed: {
    persons() {
      return (
        this.$store.state.users
          ?.filter(
            user =>
              !!user.roles.find(
                role =>
                  role.category.id === "00000000-0000-0000-0000-119000000000"
              ) && user.principal_id === this.currentPrincipal
          )
          ?.map(user => ({
            ...user,
            title: `${user.first_name} ${user.last_name}`
          })) || []
      );
    },
    defects() {
      return this.$store.state.defects;
    },
    defectCategories() {
      return this.$store.state.defectCategories;
    },
    defectCount() {
      return this.$store.state.defectCount;
    },
    currentPrincipal() {
      return this.$store.state.selectedPrincipal.id;
    },
    geoLocation() {
      return this.previewItem.location?.geo?.coordinates ?? [];
    }
  }
};
